import { MarketPeriodInterface } from 'features/dispatcher/dispatcherTypes';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { validateMarketDayValueFormat } from './marketDay';

import { fetchMarketTimeRules } from 'features/dispatcher/thunks/dispatcherThunks';

type MarketDayRecord = {
  marketPeriods: MarketPeriodInterface[];
  marketHours: MarketPeriodInterface[];
};

type MarketDayRecordsMap = {
  [marketDay: string]: MarketDayRecord | undefined;
};
type MarketDayContextState = {
  getMarketPeriodsForMarketDay: (
    date: string
  ) => Promise<MarketDayRecord | undefined>;
};

const initialState: MarketDayContextState = {
  getMarketPeriodsForMarketDay: async () => undefined
};

const MarketDayContext = createContext<MarketDayContextState>(initialState);

export const MarketDayContextProvider: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const [marketDayRecords, setMarketDayRecords] = useState<MarketDayRecordsMap>(
    {}
  );

  const fetchAndSetMarketRules = async (marketDay: string) => {
    const marketTimeRules = await fetchMarketTimeRules(marketDay);
    if (!marketTimeRules) {
      return undefined;
    }
    const record = {
      marketPeriods: marketTimeRules.marketPeriods,
      marketHours: marketTimeRules?.marketHours
    };
    setMarketDayRecords((prev) => ({
      ...prev,
      [marketDay]: record
    }));
    return marketTimeRules;
  };

  const getMarketPeriodsForMarketDay = useCallback(
    async (marketDay: string): Promise<MarketDayRecord | undefined> => {
      if (!validateMarketDayValueFormat(marketDay)) {
        logInvalidMarketDayFormat(marketDay);
        return;
      }
      const marketDayRecord = marketDayRecords[marketDay];
      if (marketDayRecord) {
        return marketDayRecord;
      }
      const fetchedMarketRules = await fetchAndSetMarketRules(marketDay);

      return fetchedMarketRules;
    },
    [marketDayRecords]
  );

  const value = useMemo(
    () => ({
      getMarketPeriodsForMarketDay
    }),
    [getMarketPeriodsForMarketDay]
  );
  return (
    <MarketDayContext.Provider value={value}>
      {children}
    </MarketDayContext.Provider>
  );
};

export const useMarketDayContext = (): MarketDayContextState => {
  const marketDayContext = useContext(MarketDayContext);
  if (!marketDayContext || marketDayContext === initialState)
    throw new Error(
      'No MarketDayContext.Provider found when calling useMarketDayContext.'
    );
  return marketDayContext;
};

const logInvalidMarketDayFormat = (marketDay: string) => {
  // eslint-disable-next-line no-console
  console.warn(
    `Invalid format for market day: ${JSON.stringify(
      marketDay
    )}, expected format "YYYY-MM-DD"`
  );
};
